import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/notifications/purge`;

const today = () => new Date().toISOString().slice(0, 10);

// Admin: apaga notificações em massa (filtradas, até uma data, ou tudo). Prévia antes de apagar.
const NotificationsPurgeDialog = ({ filters = {}, onDone }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('filtered');
  const [before, setBefore] = useState(today());
  const [preview, setPreview] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const hasFilters = Boolean(filters.pm || filters.status || filters.tipo || filters.dateFrom || filters.dateTo);

  const previewBody = JSON.stringify(mode === 'filtered' ? { pm: filters.pm || null, status: filters.status || null, tipo: filters.tipo || null, date_from: filters.dateFrom || null, date_to: filters.dateTo || null } : mode === 'before' ? { date_to: before, everything: true } : { everything: true });

  const body = () => JSON.parse(previewBody);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setAck(false);
    axios.post(`${API}/preview`, JSON.parse(previewBody)).then((r) => setPreview(r.data)).catch((e) => toast.error(e.response?.data?.detail || 'Erro ao calcular prévia'));
  }, [open, previewBody]);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(API, body());
      toast.success(`${data.deleted} notificações apagadas. 🧹`);
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao apagar'); } finally { setBusy(false); }
  };

  const Option = ({ value, label, hint, children }) => (
    <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${mode === value ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`purge-mode-${value}`}>
      <input type="radio" name="purge-mode" value={value} checked={mode === value} onChange={() => setMode(value)} className="mt-1 accent-red-600" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{label}</p>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
        {children}
      </div>
    </label>
  );

  return (
    <>
      <Button onClick={() => setOpen(true)} size="sm" variant="outline" className="text-xs border-red-300 text-red-700 hover:bg-red-50" data-testid="purge-notifications-btn">
        <Trash2 className="h-3.5 w-3.5 mr-1" /> Limpar notificações
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="purge-notifications-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 className="h-4 w-4 text-red-600" /> Limpar notificações antigas</DialogTitle>
            <DialogDescription>Apaga permanentemente do Histórico e do sino de todos os usuários. Não há como desfazer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Option value="filtered" label="Só o que está filtrado agora" hint={hasFilters ? 'Usa os filtros atuais do Histórico (PM, status, tipo, datas).' : 'Nenhum filtro ativo → equivale a todas as atividades (exceto alertas de contas).'} />
            <Option value="before" label="Tudo até uma data">
              <div className="mt-2 flex items-center gap-2">
                <Label className="text-xs text-slate-600">Até</Label>
                <Input type="date" value={before} onChange={(e) => setBefore(e.target.value)} className="h-9 w-44" data-testid="purge-before-date" />
              </div>
            </Option>
            <Option value="all" label="TUDO" hint="Todas as notificações do sistema, de todos os usuários (inclui alertas de contas)." />
          </div>

          <div className="rounded-lg bg-slate-50 border p-3 text-sm" data-testid="purge-preview">
            {!preview ? <span className="inline-flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Calculando…</span> : (
              <span>Serão apagadas <b className="text-red-700" data-testid="purge-preview-count">{preview.to_delete}</b> de {preview.total_in_db} notificações.</span>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="accent-red-600" data-testid="purge-ack" />
            <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Entendo que esta ação é irreversível</span>
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="purge-cancel">Cancelar</Button>
            <Button onClick={purge} disabled={busy || !ack || !preview || preview.to_delete === 0} className="bg-red-600 hover:bg-red-700" data-testid="purge-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1" /> Apagar {preview?.to_delete ?? ''}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationsPurgeDialog;
